import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  ADMIN_ROUTES,
  DEFAULT_ADMIN_TAB,
  getAdminRouteByPath,
} from "../config/adminRoutes";

const KNOWN_TABS = new Set(ADMIN_ROUTES.map((r) => r.path));

// Retired standalone pages now live as tabs inside marketing-hub.
// Redirect old deep links (marketing-posts, system-banner-management, etc.)
// to /marketing-hub?tab=<key> so bookmarks keep working.
const LEGACY_REDIRECTS = {
  "marketing-posts": "posts",
  "system-banner-management": "public-banner",
  "company-banner-moderation": "company-banners",
  "stories-management": "stories-legacy",
};

// URL → tab. Accepts both `/{tab}` and legacy `/admin/{tab}` (habit).
function tabFromPath(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return DEFAULT_ADMIN_TAB;
  const first = parts[0] === "admin" && parts.length > 1 ? parts[1] : parts[0];
  return getAdminRouteByPath(first) ? first : DEFAULT_ADMIN_TAB;
}

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = tabFromPath(location.pathname);

  useEffect(() => {
    const token = localStorage.getItem("google_id_token");
    if (token) {
      setIsAuthenticated(true);
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Normalize the URL so it always looks like /{tab}. This also rewrites
  // legacy /admin/{tab} deep links to /{tab} without a full reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    // Retired-page compat: /marketing-posts → /marketing-hub?tab=posts, etc.
    const parts = location.pathname.split("/").filter(Boolean);
    const firstSeg = parts[0] === "admin" && parts.length > 1 ? parts[1] : parts[0];
    // Бұрынғы «Пакеты продвижения и тарифы» → /tariffs
    if (firstSeg === "promotion-packages") {
      navigate("/tariffs", { replace: true });
      return;
    }
    const legacyTab = LEGACY_REDIRECTS[firstSeg];
    if (legacyTab) {
      navigate({ pathname: "/marketing-hub", search: `?tab=${legacyTab}` }, { replace: true });
      return;
    }
    const desired = `/${activeTab}`;
    if (location.pathname !== desired) {
      navigate(desired, { replace: true });
    }
  }, [isAuthenticated, activeTab, location.pathname, navigate]);

  const setActiveTab = useCallback(
    (tab) => {
      if (!KNOWN_TABS.has(tab)) return;
      navigate(`/${tab}`);
    },
    [navigate]
  );

  const handleLogout = () => {
    localStorage.removeItem("google_id_token");
    setIsAuthenticated(false);
    navigate("/", { replace: true });
    window.location.reload();
  };

  if (!isAuthenticated) {
    return (
      <div className="dark-theme flex items-center justify-center min-h-screen bg-gray-900">
        <p className="text-xl text-gray-200">Вы не авторизованы</p>
      </div>
    );
  }

  return (
    <AdminLayout
      onLogout={handleLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
}

export default AdminDashboard;
